import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Paper, Stack, Button, ButtonGroup } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import GoldenArrow from '../components/common/GoldenArrow';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, ICellRendererParams } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import apiService from '../services/api.service';
import PRGlobalPicker from '../components/common/PRGlobalPicker';
import { transformPRForCopy } from '../utils/prCopyTransformer';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const toDDMMYYYY = (v: any): string => {
  const s = String(v || '').trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
  const compact = s.match(/^(\d{8})$/);
  if (compact) return `${s.slice(6,8)}-${s.slice(4,6)}-${s.slice(0,4)}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
};

const Inbox: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, myRes, approverRes, pendingPORes, myPORes, approverPORes] = await Promise.all([
        apiService.getPendingApprovals(),
        apiService.getMyPRs(),
        apiService.getApproverPRs(),
        apiService.getPendingApprovalsPO(),
        apiService.getMyPOs(),
        apiService.getApproverPOs(),
      ]);
      let pending = ((pendingRes as any)?.data || pendingRes || []).map((r: any) => ({ ...r, _docType: 'PR' }));
      const pendingPO = ((pendingPORes as any)?.data || pendingPORes || []).map((r: any) => ({ ...r, _docType: 'PO' }));
      let myRejected = ((myRes as any)?.data || myRes || []).filter((r: any) => {
        const st = String(r.status || '').toLowerCase();
        const inbox = String(r.inboxstatus || '').toLowerCase();
        return st === 'rejected' && inbox !== 'completed';
      }).map((r: any) => ({ ...r, _docType: 'PR' }));
      let approverRejected = ((approverRes as any)?.data || approverRes || []).filter((r: any) => {
        const st = String(r.status || '').toLowerCase();
        const inbox = String(r.inboxstatus || '').toLowerCase();
        return st === 'rejected' && inbox !== 'completed';
      }).map((r: any) => ({ ...r, _docType: 'PR' }));
      const myPORejected = ((myPORes as any)?.data || myPORes || []).filter((r: any) => {
        const st = String(r.status || '').toLowerCase();
        const inbox = String(r.inboxstatus || '').toLowerCase();
        return st === 'rejected' && inbox !== 'completed';
      }).map((r: any) => ({ ...r, _docType: 'PO' }));
      const approverPORejected = ((approverPORes as any)?.data || approverPORes || []).filter((r: any) => {
        const st = String(r.status || '').toLowerCase();
        const inbox = String(r.inboxstatus || '').toLowerCase();
        return st === 'rejected' && inbox !== 'completed';
      }).map((r: any) => ({ ...r, _docType: 'PO' }));

      let list = [...pending, ...pendingPO, ...myRejected, ...approverRejected, ...myPORejected, ...approverPORejected];
      const seen = new Set<string>();
      list = list.filter((r: any) => {
        const docType = String(r._docType || 'PR');
        const id = Number(docType === 'PO' ? (r.poid || r.POID || 0) : (r.prid || r.PRID || 0));
        if (!id) return false;
        const key = `${docType}:${id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      list = list.filter((r: any) => {
        const st = String(r.status || '').toLowerCase();
        const posted = st.includes('posted') || Number(r.sappodocnum || r.SAPPODocNum || 0) > 0;
        const approved = st === 'approved';
        return !approved && !posted;
      });
      setRows(list);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCopyPR = useCallback(async (prId: number) => {
    try {
      const response = await apiService.getPR(prId);
      const prData = (response as any)?.data || response;
      const copiedData = transformPRForCopy(prData);

      navigate('/create-pr', {
        state: {
          copiedData,
          sourcePRId: prId
        }
      });
    } catch (error: any) {
      console.error('Failed to copy PR:', error);
    }
  }, [navigate]);

  const [columnDefs] = useState([
    {
      field: 'docnumber',
      headerName: 'Doc Number',
      width: 200,
      cellRenderer: (params: ICellRendererParams) => {
        const isPO = String(params.data._docType || 'PR') === 'PO';
        const docNo = isPO ? (params.data.ponumber || '') : (params.data.prnumber || '');
        const docId = Number(
          isPO
            ? (params.data.poid || params.data.POID || params.data.id || 0)
            : (params.data.prid || params.data.PRID || params.data.id || 0)
        );
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, whiteSpace: 'nowrap' }} noWrap>
                {docNo}
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              {isPO ? (
                <GoldenArrow docType="PO" docId={docId} poId={docId} onAfterAction={load} />
              ) : (
                <GoldenArrow docType="PR" docId={docId} prId={docId} onAfterAction={load} />
              )}
            </Box>
          </Box>
        );
      }
    },
    {
      field: '_docType',
      headerName: 'Doc Type',
      width: 90,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
          {String(params.data._docType || 'PR')}
        </Typography>
      )
    },
    { 
      field: 'docdate',
      headerName: 'Doc Date', 
      width: 120,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
          {toDDMMYYYY(params.data._docType === 'PO' ? params.data.podate : params.data.prdate)}
        </Typography>
      )
    },
    { 
      field: 'requestedbyname', 
      headerName: 'Requester', 
      width: 160,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400 }} noWrap title={params.data.requestedbyname}>
          {params.data.requestedbyname}
        </Typography>
      )
    },
    { 
      field: 'requisitiontype', 
      headerName: 'Type', 
      width: 110,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
          {params.data.requisitiontype || ''}
        </Typography>
      )
    },
    { 
      field: 'allocationtype', 
      headerName: 'Allocation Type', 
      width: 130,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
          {params.data.allocationtype || ''}
        </Typography>
      )
    },
    { 
      field: 'allocationcode', 
      headerName: 'Allocation Code', 
      width: 140,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
          {params.data.allocationcode || ''}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      cellRenderer: (params: ICellRendererParams) => {
        const status = String(params.data.status || '').toLowerCase();
        const isRejected = status === 'rejected';
        const isPending = status.startsWith('pending');
        const bgColor = isRejected ? '#FFEBEE' : isPending ? '#FFF3E0' : '#E8F5E9';
        const textColor = isRejected ? '#C62828' : isPending ? '#E65100' : '#2E7D32';
        return (
          <Box sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: bgColor, width: '100%' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: textColor }} noWrap>
              {params.data.status || ''}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'currentstep',
      headerName: 'Step',
      width: 80,
      cellRenderer: (params: ICellRendererParams) => {
        const step = params.data.currentstep;
        return step ? (
          <Typography sx={{ fontSize: '12px', fontWeight: 400, textAlign: 'center', display: 'block' }}>
            {step}
          </Typography>
        ) : null;
      }
    },
    { 
      field: 'requireddate', 
      headerName: 'Needed By', 
      width: 120,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
          {toDDMMYYYY(params.data.requireddate)}
        </Typography>
      )
    },
    {
      field: 'totalamount',
      headerName: 'Amount',
      width: 130,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 600, textAlign: 'right', display: 'block' }}>
          {Number(params.data.totalamount || 0).toFixed(2)} {params.data.currency || ''}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      suppressSizeToFit: true,
      cellRenderer: (params: ICellRendererParams) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <ButtonGroup sx={{ height: 24 }}>
            <Button 
              size="small" 
              sx={{ minWidth: 32, height: 24, fontSize: '11px', fontWeight: 500 }}
              onClick={() => {
                if (params.data._docType === 'PO') {
                  navigate(`/po/${Number(params.data.poid)}`);
                } else {
                  window.dispatchEvent(new CustomEvent('pr-view-click', { detail: { prId: params.data.prid } }));
                }
              }}
            >
              View
            </Button>
            {params.data._docType !== 'PO' && (
              <Button 
                size="small" 
                sx={{ minWidth: 32, height: 24, fontSize: '11px', fontWeight: 500 }}
                onClick={() => handleCopyPR(params.data.prid)}
              >
                Copy
              </Button>
            )}
          </ButtonGroup>
        </Box>
      )
    }
  ]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onUpdated = () => { void load(); };
    window.addEventListener('pr-updated', onUpdated as any);
    return () => { window.removeEventListener('pr-updated', onUpdated as any); };
  }, [load]);

  useEffect(() => {
    const onUpdated = () => { void load(); };
    window.addEventListener('po-updated', onUpdated as any);
    return () => { window.removeEventListener('po-updated', onUpdated as any); };
  }, [load]);

  useEffect(() => {
    const onCompanyChanged = () => { void load(); };
    window.addEventListener('company-changed', onCompanyChanged as any);
    return () => { window.removeEventListener('company-changed', onCompanyChanged as any); };
  }, [load]);

  return (
    <>
      <Paper
        component="section"
        aria-labelledby="inbox-heading"
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          height: 'calc(100vh - 100px)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Inter', 'IBM Plex Sans', sans-serif",
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={{ xs: 1.5, sm: 0 }}
          mb={2}
        >
          <Typography
            component="h1"
            id="inbox-heading"
            variant="h5"
            sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, color: '#1a1a1a' }}
          >
            Approval Inbox
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="contained"
              onClick={() => setPickerOpen(true)}
              sx={{ flex: { xs: 1, sm: 'unset' } }}
            >
              Search PR
            </Button>
            <Button
              variant="outlined"
              onClick={load}
              disabled={loading}
              sx={{ flex: { xs: 1, sm: 'unset' } }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
        <Box sx={{ width: '100%', flexGrow: 1, minHeight: 0, overflowX: 'auto' }}>
          <div
            className="ag-theme-alpine"
            role="grid"
            aria-label="Approval inbox"
            style={{
              height: '100%',
              width: '100%',
              minWidth: 720,
              border: '2px solid #F0AB00',
              fontFamily: "'Inter', 'IBM Plex Sans', sans-serif",
            }}
          >
            <AgGridReact
              rowData={rows}
              columnDefs={columnDefs}
              domLayout="normal"
              rowHeight={36}
              headerHeight={40}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                flex: 0,
                minWidth: 80
              }}
              pagination={true}
              paginationPageSize={25}
              paginationPageSizeSelector={[25, 50, 100]}
              suppressPaginationPanel={false}
              getRowId={(params: any) => {
                const isPO = String(params.data?._docType || 'PR') === 'PO';
                const id = isPO ? (params.data.poid || params.data.POID || params.data.id) : (params.data.prid || params.data.PRID || params.data.id);
                return `${isPO ? 'PO' : 'PR'}:${id}`;
              }}
              getRowStyle={(params: any) => ({
                backgroundColor: (params.node.rowIndex ?? 0) % 2 === 0 ? '#FFFFFF' : '#F8F8F8'
              })}
              theme="legacy"
              overlayLoadingTemplate='<span></span>'
              overlayNoRowsTemplate='<div style="padding: 20px; color: #666; font-family: Inter, sans-serif;">No records to display</div>'
              defaultColGroupDef={{
                headerClass: 'ag-header-cell'
              }}
            />
          </div>
        </Box>
        <PRGlobalPicker open={pickerOpen} onClose={() => setPickerOpen(false)} context="inbox" />
      </Paper>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');

        .ag-header-cell {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          color: #1a1a1a !important;
          background: #f5f5f5 !important;
          border-bottom: 2px solid #F0AB00 !important;
        }

        .ag-header-cell-label {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
        }

        .ag-cell {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
          font-size: 12px !important;
          font-weight: 400 !important;
          color: #333333 !important;
          padding: 8px 12px !important;
        }

        .ag-row:hover .ag-cell {
          background-color: #e3f2fd !important;
        }

        .ag-paging-panel {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
          background: #f5f5f5 !important;
          border-top: 2px solid #F0AB00 !important;
          padding: 8px 12px !important;
        }

        .ag-paging-button {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          color: #0a6ed1 !important;
          background: #ffffff !important;
          border: 1px solid #e0e0e0 !important;
          border-radius: 4px !important;
          padding: 6px 12px !important;
        }

        .ag-paging-button:hover {
          background: #f0f0f0 !important;
          border-color: #0a6ed1 !important;
        }

        .ag-paging-button[disabled] {
          color: #cccccc !important;
          background: #f9f9f9 !important;
          border-color: #e0e0e0 !important;
        }
      `}</style>
    </>
  );
};

export default Inbox;
